import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_business-match-hub-1/artifacts/ly54u1mk_7A4F7B0F-B2FC-4B29-8962-F57D33FF73A6.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Industries', path: '/industries' },
  { name: 'About', path: '/about' },
  { name: 'Government', path: '/government' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="23rd Industries" 
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <span className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                23RD ENVIRONMENTAL UNITS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link px-4 py-2 text-sm uppercase tracking-widest font-mono transition-colors ${
                  location.pathname === link.path
                    ? 'text-orange-500'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA & Phone */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:8172109693" 
              className="flex items-center gap-2 text-zinc-400 hover:text-orange-500 transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-mono text-sm">(817) 210-9693</span>
            </a>
            <Link
              to="/contact"
              className="btn-primary px-6 py-2.5 text-sm"
              data-testid="header-cta"
            >
              Schedule Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-zinc-950 border-b border-zinc-800"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-6 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-sm uppercase tracking-widest font-mono transition-colors ${
                    location.pathname === link.path
                      ? 'text-orange-500 bg-zinc-900'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-zinc-800 mt-4">
                <a 
                  href="tel:8172109693" 
                  className="flex items-center gap-2 px-4 py-3 text-orange-500"
                >
                  <Phone className="w-4 h-4" />
                  <span className="font-mono">(817) 210-9693</span>
                </a>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block mt-2 btn-primary text-center py-3"
                >
                  Schedule Demo
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
